import { attachmentIdsFingerprint } from '@/lib/realtime/message-attachments';

/**
 * Backend `ThreadMembershipChangeBody` actions plus `ThreadTaskEventBody` actions
 * (messaging/thread_events.go) — both arrive on the same user.threads.* subject.
 */
const THREAD_MEMBERSHIP_ACTIONS = new Set([
  'created',
  'updated',
  'deleted',
  'taskCreated',
  'taskUpdated',
  'taskDeleted',
]);

function normalizeMessageBodyObject(body) {
  if (body == null) {
    return null;
  }
  if (typeof body === 'string') {
    const trimmed = body.trim();
    if (!trimmed) {
      return null;
    }
    try {
      const parsed = JSON.parse(trimmed);
      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  if (typeof body === 'object' && !Array.isArray(body)) {
    return body;
  }
  return null;
}

/**
 * Parses `user.threads` membership/task notifications (system message body: action +
 * threadId, plus taskId for task* actions).
 * @returns {{ action: string, threadId: string, taskId?: string } | null}
 */
export function getThreadMembershipChangeDetail(message) {
  if (String(message?.type || '').trim() !== 'system') {
    return null;
  }
  const body = normalizeMessageBodyObject(message?.body);
  if (!body) {
    return null;
  }
  const action = String(body.action || '').trim();
  const threadId = String(body.threadId || '').trim();
  if (!THREAD_MEMBERSHIP_ACTIONS.has(action) || !threadId) {
    return null;
  }
  const taskId = String(body.taskId || '').trim();
  return taskId ? { action, threadId, taskId } : { action, threadId };
}

export function isThreadMembershipChangeMessage(message) {
  return getThreadMembershipChangeDetail(message) != null;
}

/**
 * Message body parsing aligned with healo_backend thread_demo (text / event fields).
 */
export function parseMessageBodyText(body) {
  if (body == null) return '';
  if (typeof body === 'string') return body;
  if (typeof body === 'object' && !Array.isArray(body)) {
    if (typeof body.event === 'string') {
      const normalizedEvent = body.event.trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
      if (!normalizedEvent) {
        return 'System event';
      }
      return normalizedEvent.charAt(0).toUpperCase() + normalizedEvent.slice(1);
    }
    if (typeof body.text === 'string') {
      return body.text;
    }
    return JSON.stringify(body);
  }
  return JSON.stringify(body);
}

export function isSystemEventMessage(message) {
  const body = message?.body;
  if (body == null || typeof body !== 'object' || Array.isArray(body)) {
    return false;
  }
  return typeof body.event === 'string' && body.event.trim();
}

/**
 * Informational line flagged in message metadata (not part of the conversational bubble stream).
 */
export function isMetadataSystemMessage(message) {
  return message?.metadata?.isSystem === true;
}

/**
 * Outbound automatic patience/acknowledgement line (metadata-driven).
 */
export function isMetadataAutoReplyMessage(message) {
  return message?.metadata?.autoReply === true;
}

/**
 * Backend event lines (`body.event`) and client/metadata-flagged informational lines.
 * Used for clustering, unread/read behavior, and notifications.
 */
export function isStandaloneSystemLineMessage(message) {
  return isSystemEventMessage(message) || isMetadataSystemMessage(message);
}

const CALL_LOG_EVENT_RE = /^(missed|declined|answered|outgoing|incoming)_(voice|video)_call$/;

/**
 * WhatsApp call log entry stamped by the gateway (contentType "call.log",
 * body {event, direction, outcome, durationSeconds, ...}). Rendered as a
 * dedicated centered timeline row, not a chat bubble.
 */
export function isCallLogMessage(message) {
  const ct =
    typeof message?.contentType === 'string' ? message.contentType.trim().toLowerCase() : '';
  if (ct === 'call.log') return true;
  const body = message?.body;
  if (body == null || typeof body !== 'object' || Array.isArray(body)) {
    return false;
  }
  return typeof body.event === 'string' && CALL_LOG_EVENT_RE.test(body.event.trim());
}

/**
 * Defensive parse of a call log body. Returns null for non-call messages.
 */
export function getCallLogDetail(message) {
  if (!isCallLogMessage(message)) return null;
  const body =
    message?.body && typeof message.body === 'object' && !Array.isArray(message.body)
      ? message.body
      : {};
  const event = typeof body.event === 'string' ? body.event.trim() : '';
  const outcome =
    typeof body.outcome === 'string' && body.outcome.trim()
      ? body.outcome.trim()
      : event.startsWith('answered')
        ? 'answered'
        : event.startsWith('declined')
          ? 'declined'
          : 'missed';
  const durationSeconds =
    Number.isFinite(body.durationSeconds) && body.durationSeconds >= 0
      ? Math.floor(body.durationSeconds)
      : null;
  return {
    event,
    direction: body.direction === 'outgoing' ? 'outgoing' : 'incoming',
    isVideo: body.isVideo === true || event.includes('_video_'),
    outcome,
    durationSeconds,
    at: typeof body.at === 'string' ? body.at : null,
  };
}

/**
 * A missed/declined incoming call needs staff follow-up: the gateway omits
 * metadata.isSystem on those, and they must pierce the system-line
 * suppression of unread flags and desktop notifications.
 */
export function isAttentionCallLogMessage(message) {
  return isCallLogMessage(message) && !isMetadataSystemMessage(message);
}

export function getMessageDedupKey(message) {
  const id = message?.id != null ? String(message.id).trim() : '';
  if (id) return id;
  const tid = String(message?.threadId ?? '').trim();
  const ext = String(message?.externalId ?? '').trim();
  if (tid && ext) return `ext:${tid}:${ext}`;
  const sender = message?.sender ? `${message.sender.userId}:${message.sender.kind}` : 'unknown';
  const attPart = attachmentIdsFingerprint(message);
  return [message?.createdAt || '', sender, parseMessageBodyText(message?.body), attPart].join('|');
}

/**
 * Finds an existing list item that represents the same logical message (stable id,
 * or threadId+externalId from upstream upserts, or legacy dedup key).
 */
export function findMessageIndexInHistory(prev, msg) {
  const msgId = String(msg?.id ?? '').trim();
  if (msgId) {
    const byId = prev.findIndex((m) => String(m?.id ?? '').trim() === msgId);
    if (byId >= 0) return byId;
  }
  const tid = String(msg?.threadId ?? '').trim();
  const ext = String(msg?.externalId ?? '').trim();
  if (tid && ext) {
    const byExt = prev.findIndex(
      (m) =>
        String(m?.threadId ?? '').trim() === tid && String(m?.externalId ?? '').trim() === ext,
    );
    if (byExt >= 0) return byExt;
  }
  const key = getMessageDedupKey(msg);
  return prev.findIndex((m) => getMessageDedupKey(m) === key);
}
